import { BookOpen, Bookmark, ExternalLink, Check } from 'lucide-react';
import { SkeletonCard } from '../ui/SkeletonCard.js';
import type { LibraryStats } from '../../services/library.js';

interface LibrarySectionProps {
  stats: LibraryStats | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function LibrarySection({ stats, isLoading, isError }: LibrarySectionProps) {
  if (isError) return null;
  if (isLoading) {
    return (
      <div>
        <h2 className="mb-4 text-sm font-semibold text-[var(--color-text)] uppercase tracking-wider">
          xdface Library
        </h2>
        <SkeletonCard />
      </div>
    );
  }

  const inProgress = stats?.inProgress ?? [];
  const completed = stats?.completed ?? [];
  const totalHighlights = stats?.totalHighlights ?? 0;
  const hasActivity = inProgress.length > 0 || completed.length > 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text)] uppercase tracking-wider">
          xdface Library
          <a
            href="https://library.xdface.net"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--color-text-muted)] hover:text-xd-action transition-colors"
            aria-label="Abrir Library"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </h2>
      </div>

      {!hasActivity ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-8 text-center">
          <BookOpen className="mx-auto w-12 h-12 text-text-subtle" />
          <p className="mt-3 font-medium text-[var(--color-text)]">Sin lecturas registradas</p>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">Empieza a leer en Library</p>
          <a
            href="https://library.xdface.net"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-2 rounded-lg border border-xd-action px-4 py-2 text-sm font-medium text-xd-action hover:bg-xd-action/10 transition-colors"
          >
            Empieza a leer en Library →
          </a>
        </div>
      ) : (
        <div className="space-y-6">
          {inProgress.length > 0 && (
            <div>
              <h3 className="mb-3 text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">
                Lecturas en progreso
              </h3>
              <div className="space-y-3">
                {inProgress.map((item) => {
                  const pct = Math.round((item.readCount / item.totalArticles) * 100);
                  return (
                    <a
                      key={item.editionPath}
                      href={`https://library.xdface.net${item.editionPath}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col gap-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 hover:border-xd-action/40 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-[var(--color-text)] truncate">
                          {item.editionTitle}
                        </p>
                        <span className="shrink-0 text-xs text-[var(--color-text-muted)]">
                          {item.readCount} de {item.totalArticles} artículos
                        </span>
                      </div>
                      <div className="xd-progress-track h-2 w-full overflow-hidden rounded-full">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${pct}%`,
                            background: 'var(--xd-gradient-action)',
                          }}
                        />
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          {completed.length > 0 && (
            <div>
              <h3 className="mb-3 text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">
                Completadas
              </h3>
              <div className="space-y-2">
                {completed.map((item) => (
                  <a
                    key={item.editionPath}
                    href={`https://library.xdface.net${item.editionPath}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 hover:border-xd-action/40 hover:shadow-sm transition-all"
                  >
                    <p className="text-sm font-medium text-[var(--color-text)] truncate">
                      {item.editionTitle}
                    </p>
                    <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-xd-success/10 px-2.5 py-0.5 text-xs font-medium text-xd-success">
                      <Check className="h-3 w-3" aria-hidden="true" /> Completado
                    </span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {totalHighlights > 0 && (
            <div className="flex items-center gap-2 pt-1">
              <Bookmark className="w-4 h-4 text-xd-action" />
              <span className="text-sm text-text-muted">
                {totalHighlights} {totalHighlights === 1 ? 'destacado' : 'destacados'} guardados
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
